import { useCallback, useEffect, useState } from 'react'

const STORAGE_KEY = 'vanguard-guia-progreso-v1'

type Store = Record<string, string[]>

function read(): Store {
  try {
    return JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? '{}') as Store
  } catch {
    return {}
  }
}

/**
 * Lista personal de progreso guardada en este navegador.
 * No consulta el inventario ni el estado real de las misiones del personaje.
 */
export function useChecklist(key: string) {
  const [checked, setChecked] = useState<Set<string>>(() => new Set(read()[key] ?? []))

  useEffect(() => {
    setChecked(new Set(read()[key] ?? []))
  }, [key])

  const persist = useCallback(
    (next: Set<string>) => {
      try {
        const store = read()
        if (next.size) store[key] = Array.from(next)
        else delete store[key]
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store))
      } catch {
        /* Sin almacenamiento disponible: el progreso dura mientras la página esté abierta. */
      }
    },
    [key],
  )

  const toggle = useCallback(
    (id: string) =>
      setChecked((prev) => {
        const next = new Set(prev)
        if (next.has(id)) next.delete(id)
        else next.add(id)
        persist(next)
        return next
      }),
    [persist],
  )

  const reset = useCallback(() => {
    setChecked(new Set())
    persist(new Set())
  }, [persist])

  return { checked, toggle, reset, isChecked: (id: string) => checked.has(id) }
}

export type Checklist = ReturnType<typeof useChecklist>
